import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { Lightbox, LightboxType } from "../../../../../../../react-core/Html/Components/Lightbox";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { ClientRecordAction } from "../../../ClientRecord/Action/ClientRecordAction";
import { PensionProtection as PensionProtectionForm } from "../../../ClientRecord/PensionProtection";
import { ClientRecordTab } from "../../../ClientRecord/Resource/Contents";
import { ClientRecordStore } from "../../../ClientRecord/Store/Variable";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";

interface IPensionProtectionProps extends IProcessComponentProps {
    label: string;
    isNewInstruction?: boolean;
    doCalculation?: VoidFunction;
}

interface IPensionProtectionState {
    isLightboxShow: boolean;
    hasResetPensionProtection: boolean; 
}

export class PensionProtection extends ProcessComponent<IPensionProtectionProps, IPensionProtectionState> {

    constructor(props: any) {
        super(props);
        this.state = {
            isLightboxShow: false,
            hasResetPensionProtection: false,
        };
    }

    private resetPensionProtectionDetails(customerId) {
        if (this.props.isNewInstruction && CommonHelper.isNullOrEmpty(this.props.data.HierarchyId)) {
            this.setState({ hasResetPensionProtection: true });
            return;
        }

        var model = ClientRecordStore.getData()[ClientRecordTab.PensionProtection];

        if (!CommonHelper.isNullOrEmpty(this.props.data.HierarchyId)) {
            ClientRecordAction.savePensionProtectionInstruction(this.props.data.HierarchyId, model, (result) => {
                if (result.length == 0) {
                    this.props.onChange({ PensionProtection: model });
                }
            });
        } else {
            ClientRecordAction.savePensionProtection(customerId, model, (result) => {
                if (result.length == 0) {
                    this.props.onChange({ PensionProtection: model });
                }
            });
        }
        
        this.setState({ hasResetPensionProtection: true });
    }

    private renderPensionProtection(data, onChange) {
        return (
            <RadiosEditor
                cssWrapper="radio-group rg-150 mb-16"
                label={"Does your client have any form of pension protection?"}
                selected={data?.HasPensionProtection ?? false}
                options={[true, false]}
                getText={(v) => (v ? "Yes" : "No")}
                onChange={(v) => {
                    if (!v) {
                        // selection is No, so need to reset all pension protection sub questions
                        this.resetPensionProtectionDetails(getQueryParameters()["customerid"]);
                    } 
                    this.onDataChange(onChange, { HasPensionProtection: v }, data);
                    this.setState({ isLightboxShow: v });
                }}
                required={"Please select"}
            />
        );
    }

    private setVisibleScrollBar() {
        document.body.style.overflow = "visible";
    }

    private isPensionProtectionDetailsAllEmpty(pensionProtection): boolean {
        return !pensionProtection || this.state.hasResetPensionProtection || !pensionProtection.HasAnyProtection;
    }

    private renderPensionProtectionForm() {
        const { data, doCalculation} = this.props;
        return (
            <Lightbox
                show={this.state.isLightboxShow}
                title="Pension protection"
                onClose={() => {
                    this.setState({ isLightboxShow: false });
                    if (data && !data.PensionProtection) {
                        this.onDataChange(this.props.onChange, { HasPensionProtection: false }, data);
                    }
                }}
                type={LightboxType.Modal}
                unclosable={false}
                noOverlay={false}
                noAutofocus={true}
                cssWrapper="is-default-lightbox"
                isClickOutsideClose={false}
            >
                <PensionProtectionForm
                    id={getQueryParameters()["customerid"]}
                    callback={() => {
                        this.setState({ isLightboxShow: false });
                        this.setVisibleScrollBar();
                        if (doCalculation) {
                            doCalculation();
                        }
                    }}
                    hideHeading={true}
                    onSaveBack={(result) => {
                        this.props.onChange({
                            PensionProtection: result,
                            HasPensionProtection: result?.HasAnyProtection == true
                        });
                        this.setState({ hasResetPensionProtection: false });
                    }}
                    hideContainerClasses={true}
                    hasReadWriteAccess={true}
                    isNewInstruction={this.props.isNewInstruction || false}
                    subaccountInstructionHierarchyId={this.props.data.HierarchyId}
                    hasResetPensionProtection={this.state.hasResetPensionProtection}
                    pensionProtection={data?.PensionProtection || null}
                />
            </Lightbox>
        );
    }

    public render(): JSX.Element {
        const { data, onChange, label, doCalculation} = this.props;
        return (
            <>
                {data.HasPensionProtection && data.PensionProtection &&
                <PensionProtectionForm
                    id={getQueryParameters()["customerid"]}
                    key={JSON.stringify(data.PensionProtection)}
                    callback={() => {
                        this.setState({ isLightboxShow: false });
                        this.setVisibleScrollBar();
                        if (doCalculation) {
                            doCalculation();
                        }
                    }}
                    hideHeading={true}
                    onSaveBack={(result) => {
                        this.props.onChange({
                            PensionProtection: result,
                            HasPensionProtection: result?.HasAnyProtection == true
                        });
                        this.setState({ hasResetPensionProtection: false });
                    }}
                    hideContainerClasses={true}
                    hasReadWriteAccess={false}
                    isNewInstruction={this.props.isNewInstruction || false}
                    subaccountInstructionHierarchyId={this.props.data.HierarchyId}
                    hasResetPensionProtection={this.state.hasResetPensionProtection}
                    pensionProtection={data?.PensionProtection || null}
                    displaySubmittedOptionsOnly={true}
                    disabled={true}
                />}
                {this.renderPensionProtection(data, onChange)}
                {this.isPensionProtectionDetailsAllEmpty(data.PensionProtection) && (
                    <div>
                        <Alert 
                            alertType={AlertType.Notification} 
                            message={"Your client currently has no pension protection associated with this account."}
                            hideCloseButton={true}
                        />
                    </div>
                )}
                {data.HasPensionProtection && data.PensionProtection && (
                    <button
                        className="edit-circle-icon-button"
                        title="Edit"
                        onClick={() => {
                            this.setState({ isLightboxShow: true });
                        }}
                    >
                        <span className="icon" aria-hidden={true}></span>
                        <span className="text">{label}</span>
                    </button>
                )}
                {this.state.isLightboxShow && this.renderPensionProtectionForm()}
            </>
        );
    }
}
